import { useState, useEffect, useMemo, useRef } from 'react';
import { DisplayEvent } from '../types/event';

interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of items
}

const DEFAULT_CONFIG: CacheConfig = {
  ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
  maxSize: 100,
};

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class OfflineCache {
  private prefix = 'loci_cache_';

  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  private isExpired(item: CacheItem<any>): boolean {
    return Date.now() - item.timestamp > item.ttl;
  }

  private cleanupExpired(): void {
    try {
      const keys = Object.keys(localStorage).filter((key) => key.startsWith(this.prefix));

      keys.forEach((key) => {
        try {
          const item = JSON.parse(localStorage.getItem(key) || '');
          if (this.isExpired(item)) {
            localStorage.removeItem(key);
          }
        } catch {
          // Remove invalid items
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  set<T>(key: string, data: T, config: Partial<CacheConfig> = {}): void {
    try {
      const finalConfig = { ...DEFAULT_CONFIG, ...config };
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        ttl: finalConfig.ttl,
      };

      localStorage.setItem(this.getKey(key), JSON.stringify(item));

      // Periodic cleanup
      if (Math.random() < 0.1) {
        this.cleanupExpired();
      }
    } catch (error) {
      console.warn('Cache set failed:', error);
      // Handle storage quota exceeded
      if (error instanceof DOMException && error.code === 22) {
        this.clearOldest();
        // Retry once
        try {
          localStorage.setItem(
            this.getKey(key),
            JSON.stringify({
              data,
              timestamp: Date.now(),
              ttl: config.ttl || DEFAULT_CONFIG.ttl,
            }),
          );
        } catch (retryError) {
          console.warn('Cache retry failed:', retryError);
        }
      }
    }
  }

  get<T>(key: string): T | null {
    try {
      const rawItem = localStorage.getItem(this.getKey(key));
      if (!rawItem) return null;

      const item: CacheItem<T> = JSON.parse(rawItem);

      if (this.isExpired(item)) {
        localStorage.removeItem(this.getKey(key));
        return null;
      }

      return item.data;
    } catch (error) {
      console.warn('Cache get failed:', error);
      return null;
    }
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.warn('Cache remove failed:', error);
    }
  }

  clear(): void {
    try {
      const keys = Object.keys(localStorage).filter((key) => key.startsWith(this.prefix));
      keys.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Cache clear failed:', error);
    }
  }

  private clearOldest(): void {
    try {
      const items = Object.keys(localStorage)
        .filter((key) => key.startsWith(this.prefix))
        .map((key) => ({
          key,
          timestamp: JSON.parse(localStorage.getItem(key) || '{}').timestamp || 0,
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 25% of items
      const toRemove = Math.ceil(items.length * 0.25);
      items.slice(0, toRemove).forEach((item) => {
        localStorage.removeItem(item.key);
      });
    } catch (error) {
      console.warn('Clear oldest failed:', error);
    }
  }

  getStats(): {
    totalItems: number;
    totalSize: number;
    expiredItems: number;
  } {
    try {
      const keys = Object.keys(localStorage).filter((key) => key.startsWith(this.prefix));

      let totalSize = 0;
      let expiredItems = 0;

      keys.forEach((key) => {
        const value = localStorage.getItem(key) || '';
        totalSize += value.length;

        try {
          const item = JSON.parse(value);
          if (this.isExpired(item)) {
            expiredItems++;
          }
        } catch {
          expiredItems++;
        }
      });

      return {
        totalItems: keys.length,
        totalSize,
        expiredItems,
      };
    } catch (error) {
      console.warn('Cache stats failed:', error);
      return { totalItems: 0, totalSize: 0, expiredItems: 0 };
    }
  }
}

// Singleton instance
const offlineCache = new OfflineCache();

// Specialized cache for events
export class EventCache {
  private cache = offlineCache;
  private readonly EVENTS_KEY = 'recent_events';
  private readonly SEARCHES_KEY = 'popular_searches';
  private readonly TRENDING_KEY = 'trending_events';

  cacheEvents(events: DisplayEvent[], location?: { lat: number; lon: number }): void {
    const key = location ? `${this.EVENTS_KEY}_${location.lat}_${location.lon}` : this.EVENTS_KEY;

    this.cache.set(key, events, { ttl: 30 * 60 * 1000 }); // 30 minutes
  }

  getCachedEvents(location?: { lat: number; lon: number }): DisplayEvent[] | null {
    const key = location ? `${this.EVENTS_KEY}_${location.lat}_${location.lon}` : this.EVENTS_KEY;

    return this.cache.get<DisplayEvent[]>(key);
  }

  cacheSearch(query: string, results: DisplayEvent[]): void {
    this.cache.set(`search_${query}`, results, { ttl: 15 * 60 * 1000 }); // 15 minutes

    // Update popular searches
    const popular = this.cache.get<string[]>(this.SEARCHES_KEY) || [];
    const updated = [query, ...popular.filter((s) => s !== query)].slice(0, 10);
    this.cache.set(this.SEARCHES_KEY, updated, { ttl: 24 * 60 * 60 * 1000 }); // 24 hours
  }

  getCachedSearch(query: string): DisplayEvent[] | null {
    return this.cache.get<DisplayEvent[]>(`search_${query}`);
  }

  getPopularSearches(): string[] {
    return this.cache.get<string[]>(this.SEARCHES_KEY) || [];
  }

  cacheTrendingEvents(events: DisplayEvent[]): void {
    this.cache.set(this.TRENDING_KEY, events, { ttl: 60 * 60 * 1000 }); // 1 hour
  }

  getTrendingEvents(): DisplayEvent[] | null {
    return this.cache.get<DisplayEvent[]>(this.TRENDING_KEY);
  }

  cacheEventDetails(eventId: string, event: DisplayEvent): void {
    this.cache.set(`event_${eventId}`, event, { ttl: 24 * 60 * 60 * 1000 }); // 24 hours
  }

  getCachedEventDetails(eventId: string): DisplayEvent | null {
    return this.cache.get<DisplayEvent>(`event_${eventId}`);
  }

  clearEventCache(): void {
    this.cache.remove(this.EVENTS_KEY);
    this.cache.remove(this.TRENDING_KEY);
  }

  getCacheStats() {
    return this.cache.getStats();
  }
}

// Hook for using event cache
export function useEventCache() {
  const cacheRef = useRef<EventCache | null>(null);

  if (!cacheRef.current) {
    cacheRef.current = new EventCache();
  }

  const cache = cacheRef.current;

  return useMemo(
    () => ({
      cacheEvents: (events: DisplayEvent[], location?: { lat: number; lon: number }) =>
        cache.cacheEvents(events, location),
      getCachedEvents: (location?: { lat: number; lon: number }) => cache.getCachedEvents(location),
      cacheSearch: (query: string, results: DisplayEvent[]) => cache.cacheSearch(query, results),
      getCachedSearch: (query: string) => cache.getCachedSearch(query),
      getPopularSearches: () => cache.getPopularSearches(),
      cacheTrendingEvents: (events: DisplayEvent[]) => cache.cacheTrendingEvents(events),
      getTrendingEvents: () => cache.getTrendingEvents(),
      cacheEventDetails: (eventId: string, event: DisplayEvent) =>
        cache.cacheEventDetails(eventId, event),
      getCachedEventDetails: (eventId: string) => cache.getCachedEventDetails(eventId),
      clearCache: () => cache.clearEventCache(),
      getCacheStats: () => cache.getCacheStats(),
    }),
    [cache],
  );
}

// Network status detection
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [connectionType, setConnectionType] = useState<string>('unknown');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    const updateConnectionType = () => {
      const connection =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;
      if (connection) {
        setConnectionType(connection.effectiveType || 'unknown');
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Listen for connection changes
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;
    if (connection) {
      connection.addEventListener('change', updateConnectionType);
      updateConnectionType();
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      if (connection) {
        connection.removeEventListener('change', updateConnectionType);
      }
    };
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    connectionType,
    isSlowConnection: ['slow-2g', '2g'].includes(connectionType),
  };
}
